import { createFileRoute } from '@tanstack/react-router';
import { useState, use, lazy, Suspense } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '@/app/appContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  Sidenav,
  SidenavButton,
  SidenavEmpty,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavList,
} from '@/app/ui/components/sidenav';
import { HugeiconsIcon } from '@hugeicons/react';
import { AiBrain02Icon, Download01Icon } from '@hugeicons/core-free-icons';
import { useTenderFeedbackDocuments } from '@/app/hooks/queries/useStandaloneTenderFeedbacks';
import {
  TenderFeedbackDocument,
  getTenderFeedbackIndexedDocumentsStatus,
} from '@/app/services/tenderFeedbacksService';
import { queryKeys } from '@/app/utils/constants';
import { Badge, BadgeDot } from '@/app/ui/components/badge';
import { getLocalizedDateDistance } from '@/app/utils/dates';
import { Card } from '@/app/ui/components/card';
import { Spinner } from '@/app/ui/components/spinner';
import { MainScrollArea } from '@/app/ui/components/main';
import { getOrganizationDocById } from '@/app/services/organizationService';
import { downloadFileFromUrl } from '@/app/utils/helpers';

const InlineDocumentPreview = lazy(
  () => import('@/app/components/shared/Preview/InlineDocumentPreview'),
);

export const Route = createFileRoute(
  '/_authed/(organization)/_layout/knowledge-base/tender-feedbacks/$tenderFeedbackId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { tenderFeedbackId } = Route.useParams();
  const locale = useLocale();
  const t = useTranslations('pages.knowledgeBase');
  const tPage = useTranslations('pages.tenderFeedbacks');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const { userMeta } = use(AppContext);
  const organizationId = userMeta.organizationId ?? '';

  const { data, isLoading } = useTenderFeedbackDocuments(organizationId, tenderFeedbackId);

  const documents = data?.documents || [];

  // fetch indexing status
  const { data: indexingStatus } = useQuery({
    queryKey: [queryKeys.INDEXING_STATUS, 'tenderFeedback', tenderFeedbackId],
    queryFn: async () => {
      const response = await getTenderFeedbackIndexedDocumentsStatus(tenderFeedbackId);
      return response?.data ?? null;
    },
    enabled: !!tenderFeedbackId,
    refetchInterval: (query) => {
      if (query.state.data?.indexedPercentage === 100) return false;
      return 5000;
    },
  });

  // auto-select first document if none selected
  const effectiveDocumentId = selectedDocumentId ?? documents[0]?.docId;

  const selectedDocument = documents.find(
    (doc: TenderFeedbackDocument) => doc.docId === effectiveDocumentId,
  );

  // fetch document details with useQuery
  const { data: selectedDocumentDetails, isLoading: isLoadingDocDetails } = useQuery({
    queryKey: ['organizationDocument', organizationId, effectiveDocumentId],
    queryFn: async () => {
      const response = await getOrganizationDocById({
        organizationId,
        documentId: effectiveDocumentId!,
      });
      return response.data;
    },
    enabled: !!effectiveDocumentId && !!organizationId,
  });

  const handleDownload = () => {
    if (!selectedDocumentDetails?.sasUri) return;
    downloadFileFromUrl(selectedDocumentDetails.sasUri, selectedDocumentDetails.name);
  };

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to={'/knowledge-base'}>
                <HugeiconsIcon icon={AiBrain02Icon} />
                {t('breadcrumbs.knowledgeBase')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {selectedDocument && (
              <>
                <BreadcrumbSeparator className="max-md:hidden" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{selectedDocument.documentFilename}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {selectedDocument && selectedDocumentDetails && (
          <HeaderButton onClick={handleDownload} className="md:inline-flex" aria-label="Download">
            <HugeiconsIcon icon={Download01Icon} />
          </HeaderButton>
        )}
      </Header>
      <MainScrollArea viewportClassName="max-lg:flex-col flex grow">
        <Sidenav className="border-r">
          <SidenavHeader>
            <SidenavHeading>
              {tPage('details.numberOfDocuments')}{' '}
              <strong className="text-foreground">{documents.length ?? 0}</strong>
            </SidenavHeading>
          </SidenavHeader>
          <SidenavList>
            {isLoading ? (
              <Spinner kind="inset" />
            ) : !documents || documents.length === 0 ? (
              <SidenavEmpty>{tPage('noDocs')}</SidenavEmpty>
            ) : (
              documents.map((doc: TenderFeedbackDocument) => (
                <SidenavItem key={doc.docId}>
                  <SidenavButton
                    active={effectiveDocumentId === doc.docId}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedDocumentId(doc.docId);
                    }}
                  >
                    <span className="lg:line-clamp-1">{doc.documentFilename}</span>
                  </SidenavButton>
                </SidenavItem>
              ))
            )}
          </SidenavList>
        </Sidenav>
        <div className="grow flex flex-col md:bg-surface-2 min-w-0">
          {selectedDocument && selectedDocumentDetails ? (
            <>
              <ul className="flex gap-2 p-4 scrollbar-hidden overflow-x-auto text-sm whitespace-nowrap pb-2">
                <Badge render={<li></li>}>
                  {selectedDocumentDetails.sizeInMegaBytes.toFixed(2)} MB
                </Badge>
                <Badge render={<li></li>}>
                  <BadgeDot
                    variant={indexingStatus?.indexedPercentage === 100 ? 'success' : 'warning'}
                  />
                  {indexingStatus?.indexedPercentage === 100
                    ? tPage('details.indexed')
                    : tPage('details.indexingPending')}
                </Badge>
                <Badge render={<li></li>}>
                  {tPage('details.created')}{' '}
                  {selectedDocumentDetails.createdAt
                    ? getLocalizedDateDistance(selectedDocumentDetails.createdAt, locale)
                    : ''}
                </Badge>
              </ul>
              <div className="p-4 grow flex flex-col min-h-0">
                <div className="grow rounded-xl overflow-hidden relative">
                  {isLoadingDocDetails ? (
                    <div className="flex items-center justify-center h-full">
                      <Spinner />
                    </div>
                  ) : (
                    <Suspense fallback={<Spinner />}>
                      <InlineDocumentPreview
                        document={{
                          url: selectedDocumentDetails.sasUri,
                          extension: selectedDocumentDetails.extension,
                        }}
                      />
                    </Suspense>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="p-4 max-md:pb-0">
              <Card className="grow p-12 text-center text-muted">
                {tPage('details.selectDocument')}
              </Card>
            </div>
          )}
        </div>
      </MainScrollArea>
    </>
  );
}
